import projectAttachmentService from "@/api/services/project-attachment-service";
import type { CreateProjectAttachmentRequest, UpdateProjectAttachmentRequest } from "@/types/project-attachment";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const PROJECT_ATTACHMENTS_QUERY_KEY = "project-attachments";

/**
 * Get all project attachments
 */
export const useProjectAttachmentsQuery = (params?: {
	per_page?: number;
	page?: number;
	project_id?: string;
	type?: string;
	project_name?: string;
}) => {
	return useQuery({
		queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY, "all", params],
		queryFn: () => projectAttachmentService.getAll(params),
	});
};

/**
 * Get attachments for a specific project
 */
export const useProjectAttachmentsByProjectQuery = (
	projectId: string,
	params?: {
		per_page?: number;
		page?: number;
		type?: string;
	},
) => {
	return useQuery({
		queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY, "by-project", projectId, params],
		queryFn: () => projectAttachmentService.getByProject(projectId, params),
		enabled: !!projectId,
	});
};

/**
 * Get attachments for a specific sales contract
 */
export const useProjectAttachmentsBySalesContractQuery = (
	salesContractId: string,
	params?: {
		per_page?: number;
		page?: number;
		type?: string;
	},
) => {
	return useQuery({
		queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY, "by-sales-contract", salesContractId, params],
		queryFn: () => projectAttachmentService.getBySalesContract(salesContractId, params),
		enabled: !!salesContractId,
	});
};

/**
 * Get a single project attachment
 */
export const useProjectAttachmentQuery = (id: string) => {
	return useQuery({
		queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY, id],
		queryFn: () => projectAttachmentService.get(id),
		enabled: !!id,
	});
};

/**
 * Upload a new project attachment
 */
export const useCreateProjectAttachmentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateProjectAttachmentRequest) => projectAttachmentService.create(data),
		onSuccess: (_, variables) => {
			// Invalidate and refetch project attachments
			queryClient.invalidateQueries({
				queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY],
			});
			// Invalidate project-specific attachments
			queryClient.invalidateQueries({
				queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY, "by-project", variables.project_id],
			});
		},
	});
};

/**
 * Update a project attachment
 */
export const useUpdateProjectAttachmentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateProjectAttachmentRequest }) => projectAttachmentService.update(id, data),
		onSuccess: (updatedAttachment) => {
			// Invalidate and refetch project attachments
			queryClient.invalidateQueries({
				queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY],
			});
			// Update the specific attachment in cache
			queryClient.setQueryData([PROJECT_ATTACHMENTS_QUERY_KEY, updatedAttachment.id], updatedAttachment);
			// Invalidate project-specific attachments
			queryClient.invalidateQueries({
				queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY, "by-project", updatedAttachment.project_id],
			});
		},
	});
};

/**
 * Delete a project attachment
 */
export const useDeleteProjectAttachmentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => projectAttachmentService.delete(id),
		onSuccess: (_, deletedId) => {
			// Invalidate and refetch project attachments
			queryClient.invalidateQueries({
				queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY],
			});
			// Remove the specific attachment from cache
			queryClient.removeQueries({
				queryKey: [PROJECT_ATTACHMENTS_QUERY_KEY, deletedId],
			});
		},
	});
};

/**
 * Download a project attachment
 */
export const useDownloadProjectAttachmentMutation = () => {
	return useMutation({
		mutationFn: async ({ id, filename }: { id: string; filename?: string }) => {
			const blob = await projectAttachmentService.download(id);

			// Create download link
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename || `attachment-${id}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		},
	});
};
